/**
 * Authentication Hooks
 */

import { useCallback } from 'react';
import { useAuth } from './AuthContext';
import { type AuthError } from './types';

// Hook for sign in
export const useSignIn = () => {
  const { signIn, isLoading, error } = useAuth();

  const handleSignIn = useCallback(async (username: string, password: string) => {
    try {
      await signIn(username, password);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error as AuthError
      };
    }
  }, [signIn]);

  return {
    signIn: handleSignIn,
    isLoading,
    error,
  };
};

// Hook for sign up
export const useSignUp = () => {
  const { signUp, isLoading, error } = useAuth();

  const handleSignUp = useCallback(async (
    username: string,
    email: string,
    password: string,
    name?: string
  ) => {
    try {
      await signUp(username, email, password, name);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error as AuthError
      };
    }
  }, [signUp]);

  return {
    signUp: handleSignUp,
    isLoading,
    error,
  };
};

// Hook for OAuth sign in using Amplify
export const useOAuthSignIn = () => {
  const { isLoading, error } = useAuth();

  const handleGoogleSignIn = useCallback(async () => {
    try {
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({ provider: 'Google' as any });
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  }, []);

  const handleOpenIDSignIn = useCallback(async (provider: string) => {
    try {
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({ provider: provider as any });
    } catch (error) {
      console.error(`${provider} sign in error:`, error);
      throw error;
    }
  }, []);

  return {
    signInWithGoogle: handleGoogleSignIn,
    signInWithOpenID: handleOpenIDSignIn,
    isLoading,
    error,
  };
};

// Hook for password reset
export const usePasswordReset = () => {
  const { forgotPassword, confirmForgotPassword, isLoading, error } = useAuth();

  const handleForgotPassword = useCallback(async (username: string) => {
    try {
      await forgotPassword(username);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error as AuthError
      };
    }
  }, [forgotPassword]);

  const handleConfirmForgotPassword = useCallback(async (
    username: string,
    code: string,
    newPassword: string
  ) => {
    try {
      await confirmForgotPassword(username, code, newPassword);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error as AuthError
      };
    }
  }, [confirmForgotPassword]);

  return {
    forgotPassword: handleForgotPassword,
    confirmForgotPassword: handleConfirmForgotPassword,
    isLoading,
    error,
  };
};

// Hook for user session
export const useUserSession = () => {
  const { session, isLoading, error, isInitialized } = useAuth();

  return {
    user: session?.user || null,
    isAuthenticated: session?.isAuthenticated || false,
    tokens: session?.tokens || null,
    isLoading,
    error,
    isInitialized,
  };
};

// Hook for sign out
export const useSignOut = () => {
  const { signOut, isLoading } = useAuth();

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error as AuthError
      };
    }
  }, [signOut]);

  return {
    signOut: handleSignOut,
    isLoading,
  };
};

// Hook for change password
export const useChangePassword = () => {
  const { changePassword, isLoading, error } = useAuth();

  const handleChangePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    try {
      await changePassword(oldPassword, newPassword);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error as AuthError
      };
    }
  }, [changePassword]);

  return {
    changePassword: handleChangePassword,
    isLoading,
    error,
  };
};

// Hook for new password challenge
export const useNewPasswordChallenge = () => {
  const { respondToNewPasswordChallenge, getPendingChallenge, isLoading, error } = useAuth();

  const handleRespondToNewPasswordChallenge = useCallback(async (newPassword: string, userAttributes?: Record<string, string>) => {
    try {
      await respondToNewPasswordChallenge(newPassword, userAttributes);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error as AuthError
      };
    }
  }, [respondToNewPasswordChallenge]);

  return {
    respondToNewPasswordChallenge: handleRespondToNewPasswordChallenge,
    getPendingChallenge,
    isLoading,
    error,
  };
};
